import * as React from 'react';
import { IconClose12, TColors } from '@cian/ui-kit';

import * as styles from './YearTooltip.css';

const DEFAULT_X_POSITION = -78;
const DEFAULT_ARROW_OFFSET = 0;

export interface IYearTooltipProps {
  content: React.ReactNode;
  onClose: () => void;
  isFullscreen: boolean;
}

/**
 * Своя временная реализация тултипов для карты.
 * Сделано, чтобы пофиксить баг с вылезанием тултипа на меню при скролле
 * https://rocketchat.cian.tech/channel/ui-kit?msg=ZtELfN5hpKgRB2Cbf
 */
export const YearTooltip: React.FC<IYearTooltipProps> = ({ content, onClose, isFullscreen }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [xPosition, setXPosition] = React.useState(DEFAULT_X_POSITION);
  const [arrowXOffset, setArrowXOffset] = React.useState(DEFAULT_ARROW_OFFSET);

  React.useLayoutEffect(() => {
    /* istanbul ignore next */
    const rect = ref.current?.getBoundingClientRect();

    /* istanbul ignore next */
    if (!rect) {
      return;
    }

    /* istanbul ignore next */
    if (rect.x < 0) {
      setXPosition(DEFAULT_X_POSITION - rect.x);
      setArrowXOffset(-rect.x);
    } else {
      setXPosition(Math.max(xPosition - rect.x, DEFAULT_X_POSITION));
      setArrowXOffset(Math.max(arrowXOffset - rect.x, DEFAULT_ARROW_OFFSET));
    }
  }, [isFullscreen]);

  const [closeButtonColor, setCloseButtonColor] = React.useState<TColors>('white_40');

  const handleMouseEnterInCloseButton = React.useCallback(() => setCloseButtonColor('white_60'), []);
  const handleMouseLeaveCloseButton = React.useCallback(() => setCloseButtonColor('white_40'), []);

  const handleCloseButtonClick: React.MouseEventHandler<HTMLDivElement> = React.useCallback(event => {
    event.stopPropagation();
    onClose();
  }, []);

  return (
    <div className={styles['base']} ref={ref} style={{ transform: `translate3d(${xPosition}px, 36px, 0)` }}>
      <div className={styles['triangle']} style={{ left: `calc(50% - 6px - ${arrowXOffset}px)` }} />
      <div
        className={styles['close']}
        onClick={handleCloseButtonClick}
        onMouseEnter={handleMouseEnterInCloseButton}
        onMouseLeave={handleMouseLeaveCloseButton}
        aria-label="Закрыть"
        role="button"
      >
        <IconClose12 color={closeButtonColor} />
      </div>
      <div className={styles['content']}>{content}</div>
    </div>
  );
};
